import { useEffect, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { revokeOtherSessions } from '../../../services/Auth/SignInActivityService';
import { readErrorEnvelope } from '../../../utils/errorEnvelope';
import PasswordInput from '../PasswordInput';

const SIGN_IN_ACTIVITY_QUERY_KEY = 'sign-in-activity';

const RevokeOtherSessionsModal = ({ show, onHide, triggeringEventUuid = null }) => {
    const queryClient = useQueryClient();

    const [currentPassword, setCurrentPassword] = useState('');
    const [fieldError, setFieldError] = useState('');
    const [topLevelError, setTopLevelError] = useState('');

    useEffect(() => {
        if (!show) {
            setCurrentPassword('');
            setFieldError('');
            setTopLevelError('');
        }
    }, [show]);

    const revokeMutation = useMutation({
        mutationFn: () => revokeOtherSessions({ currentPassword, triggeringEventUuid }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [SIGN_IN_ACTIVITY_QUERY_KEY] });
            toast.success('Other sessions have been signed out.');
            onHide();
        },
        onError: (error) => {
            const envelope = readErrorEnvelope(error);
            if (envelope?.errors?.current_password) {
                setFieldError(Array.isArray(envelope.errors.current_password)
                    ? envelope.errors.current_password[0]
                    : envelope.errors.current_password);
                setTopLevelError('');
                return;
            }
            setFieldError('');
            setTopLevelError(envelope?.message || 'Could not sign out other sessions. Please try again.');
        },
    });

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!currentPassword.trim()) {
            setFieldError('Please enter your current password.');
            return;
        }
        setFieldError('');
        setTopLevelError('');
        revokeMutation.mutate();
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Sign out other sessions</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <p className="mb-3">
                        This signs out every other browser session on your account.
                        This session stays signed in.
                    </p>
                    {triggeringEventUuid && (
                        <p className="text-muted small mb-3">
                            Linked to the event you reported as not you.
                        </p>
                    )}
                    {topLevelError && (
                        <div className="alert alert-danger" role="alert">
                            {topLevelError}
                        </div>
                    )}
                    <Form.Group controlId="revoke-other-sessions-password">
                        <Form.Label>Current password</Form.Label>
                        <PasswordInput
                            id="revoke-other-sessions-password"
                            className={fieldError ? 'form-control is-invalid' : 'form-control'}
                            autoComplete="current-password"
                            value={currentPassword}
                            onChange={(event) => {
                                setCurrentPassword(event.target.value);
                                if (fieldError) setFieldError('');
                            }}
                            disabled={revokeMutation.isPending}
                            autoFocus
                        />
                        {fieldError && (
                            <div className="invalid-feedback d-block">
                                {fieldError}
                            </div>
                        )}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={onHide}
                        disabled={revokeMutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="danger"
                        disabled={revokeMutation.isPending}
                    >
                        {revokeMutation.isPending ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                Signing out...
                            </>
                        ) : (
                            'Sign out other sessions'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default RevokeOtherSessionsModal;
